import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Card,
  CardContent,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { RateReview, Search, Star } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import { useAuth } from '../contexts/AuthContext';
import { formatJoinedDate, getAvatarColor, getUserInitials } from '../utils/userHelpers';
import { isOwnerRole } from '../utils/roles';

interface ReviewUser {
  _id: string;
  name: string;
  email: string;
  image?: string;
  phoneNumber?: string;
}

interface ReviewStudio {
  _id: string;
  studioName: string;
}

interface Review {
  _id: string;
  /** The STUDIO score. null when the member rated only the class — never 0. */
  rating?: number | null;
  /** The CLASS score — which is the instructor's. Absent on older reviews. */
  sessionRating?: number | null;
  review: string;
  user: ReviewUser;
  studio: ReviewStudio;
  instructor?: { _id: string; name: string; image?: string } | null;
  session?: { _id: string; className?: string; date?: string } | null;
  /** The class came from the booking, not the review's own snapshot. */
  classResolvedFromBooking?: boolean;
  booking?: string;
  createdAt: string;
  updatedAt: string;
}

type RatingBreakdown = Record<string, number>;

const PAGE_SIZE = 20;
const STUDIO_DISCOVERY_LIMIT = 100;

function extractUniqueStudios(reviews: Review[]): ReviewStudio[] {
  const map = new Map<string, ReviewStudio>();
  for (const review of reviews) {
    if (review.studio?._id && review.studio?.studioName) {
      map.set(review.studio._id, {
        _id: review.studio._id,
        studioName: review.studio.studioName,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.studioName.localeCompare(b.studioName));
}

function mergeStudios(existing: ReviewStudio[], incoming: ReviewStudio[]): ReviewStudio[] {
  const map = new Map<string, ReviewStudio>();
  for (const studio of [...existing, ...incoming]) {
    if (studio._id) map.set(studio._id, studio);
  }
  return Array.from(map.values()).sort((a, b) => a.studioName.localeCompare(b.studioName));
}

function truncateText(text: string, maxLength = 80): string {
  if (!text?.trim()) return '—';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}…`;
}

function StarRatingDisplay({ rating }: { rating: number }) {
  return (
    <Box display="flex" alignItems="center" gap={0.25}>
      {[1, 2, 3, 4, 5].map(star => (
        <Typography
          key={star}
          component="span"
          sx={{
            color: star <= rating ? colors.primary : colors.greyText3,
            fontSize: '0.95rem',
            lineHeight: 1,
          }}
        >
          ★
        </Typography>
      ))}
      <Typography variant="body2" color="text.secondary" ml={0.5}>
        {rating}
      </Typography>
    </Box>
  );
}

function RatingBreakdownCard({ breakdown, total }: { breakdown: RatingBreakdown; total: number }) {
  const maxCount = Math.max(...[5, 4, 3, 2, 1].map(star => breakdown[String(star)] || 0), 1);

  return (
    <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" fontWeight={500} mb={1.5}>
          Rating breakdown
        </Typography>
        <Box display="flex" flexDirection="column" gap={0.75}>
          {[5, 4, 3, 2, 1].map(star => {
            const count = breakdown[String(star)] || 0;
            const widthPct = total > 0 ? (count / maxCount) * 100 : 0;
            return (
              <Box key={star} display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 24, flexShrink: 0 }}>
                  {star}★
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    height: 8,
                    bgcolor: colors.backgroundTertiary,
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${widthPct}%`,
                      height: '100%',
                      bgcolor: colors.primary,
                      borderRadius: 1,
                      minWidth: count > 0 ? 4 : 0,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ width: 28, textAlign: 'right' }}>
                  {count}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}

function StatCard({
  title,
  value,
  subtitle,
  icon,
  iconBg,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  iconBg: string;
}) {
  return (
    <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box flex={1}>
            <Typography variant="body2" color="text.secondary" fontWeight={500} mb={0.5}>
              {title}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.75}>
              <Typography variant="h4" fontWeight={700} color="text.primary">
                {value}
              </Typography>
              {title === 'Average rating' && (
                <Star sx={{ color: colors.primary, fontSize: 28, mt: 0.25 }} />
              )}
            </Box>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: iconBg, width: 52, height: 52, borderRadius: 2 }}>{icon}</Avatar>
        </Box>
      </CardContent>
    </Card>
  );
}

const ReviewsPage: React.FC = () => {
  const { admin } = useAuth();
  /**
   * A studio owner / manager / instructor sees only their own studio, scoped
   * server-side by their token — so the Studio column and the studio filter are
   * noise for them, not a choice.
   */
  const isStudioUser =
    isOwnerRole(admin?.role) ||
    admin?.role === 'manager' ||
    admin?.role === 'instructor';

  const [reviews, setReviews] = useState<Review[]>([]);
  const [averageRating, setAverageRating] = useState(0);
  /** The denominator behind `averageRating` — NOT the review count. */
  const [totalStudioRatings, setTotalStudioRatings] = useState(0);
  /** null, never 0 — no class ratings yet is not a rating of zero. */
  const [averageSessionRating, setAverageSessionRating] = useState<number | null>(null);
  const [totalSessionRatings, setTotalSessionRatings] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [ratingBreakdown, setRatingBreakdown] = useState<RatingBreakdown>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [listTotal, setListTotal] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [studioFilter, setStudioFilter] = useState('');
  const [studios, setStudios] = useState<ReviewStudio[]>([]);

  const filtersRef = useRef({ search: '', rating: 'all', studioId: '' });
  filtersRef.current = { search: appliedSearch, rating: ratingFilter, studioId: studioFilter };

  const fetchReviews = useCallback(
    async (
      page: number,
      filters: { search: string; rating: string; studioId: string },
      options?: { updateStudios?: boolean },
    ) => {
      try {
        setIsLoading(true);
        setError(null);

        const params: Record<string, string | number> = {
          page,
          limit: PAGE_SIZE,
        };
        const trimmedSearch = filters.search.trim();
        if (trimmedSearch.length >= 2) params.search = trimmedSearch;
        if (filters.rating !== 'all') params.rating = Number(filters.rating);
        if (filters.studioId) params.studioId = filters.studioId;

        const response = await apiService.getReviews(params);
        setReviews(response.reviews || []);
        setAverageRating(response.averageRating ?? 0);
        setTotalStudioRatings(response.totalStudioRatings ?? 0);
        setAverageSessionRating(response.averageSessionRating ?? null);
        setTotalSessionRatings(response.totalSessionRatings ?? 0);
        setTotalReviews(response.totalReviews ?? 0);
        setRatingBreakdown(response.ratingBreakdown || {});
        setTotalPages(response.pages || 1);
        setListTotal(response.listTotal ?? response.totalReviews ?? 0);
        setCurrentPage(page);

        const isUnfiltered =
          trimmedSearch.length < 2 && filters.rating === 'all' && !filters.studioId;
        if (options?.updateStudios || isUnfiltered) {
          const fromReviews = extractUniqueStudios(response.reviews || []);
          setStudios(prev => mergeStudios(prev, fromReviews));
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch reviews');
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const activeFilters = useCallback(
    () => ({
      search: appliedSearch,
      rating: ratingFilter,
      studioId: studioFilter,
    }),
    [appliedSearch, ratingFilter, studioFilter],
  );

  const loadStudiosFromReviews = useCallback(async () => {
    try {
      const response = await apiService.getReviews({ page: 1, limit: STUDIO_DISCOVERY_LIMIT });
      setStudios(extractUniqueStudios(response.reviews || []));
    } catch {
      // Non-blocking — table fetch still runs
    }
  }, []);

  useEffect(() => {
    loadStudiosFromReviews();
    fetchReviews(1, { search: '', rating: 'all', studioId: '' }, { updateStudios: true });
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    const applySearch = (input: string) => {
      const trimmed = input.trim();
      const search = trimmed.length >= 2 ? trimmed : '';
      setAppliedSearch(search);
      setCurrentPage(1);
      fetchReviews(1, {
        search,
        rating: filtersRef.current.rating,
        studioId: filtersRef.current.studioId,
      });
    };

    if (value === '') {
      applySearch('');
      return;
    }

    searchDebounceRef.current = setTimeout(() => applySearch(value), 400);
  };

  const handleRatingFilterChange = (value: string) => {
    setRatingFilter(value);
    setCurrentPage(1);
    fetchReviews(1, { search: appliedSearch, rating: value, studioId: studioFilter });
  };

  const handleStudioFilterChange = (studioId: string) => {
    setStudioFilter(studioId);
    setCurrentPage(1);
    fetchReviews(1, { search: appliedSearch, rating: ratingFilter, studioId });
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    fetchReviews(page, activeFilters());
  };

  const fiveStarCount = ratingBreakdown['5'] || 0;

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} color={colors.textPrimary} mb={3}>
        Reviews
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stat cards */}
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }}
        gap={2}
        mb={3}
      >
        {isLoading && reviews.length === 0 ? (
          <>
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
          </>
        ) : (
          <>
            <StatCard
              title="Average rating"
              value={totalStudioRatings === 0 ? '—' : averageRating.toFixed(1)}
              // The count this average was computed from, not the review count.
              // A member who left the studio blank is absent from BOTH — never
              // a zero in the numerator, never a body in the denominator.
              subtitle={
                totalStudioRatings === 0
                  ? 'no studio ratings yet'
                  : `out of 5, from ${totalStudioRatings.toLocaleString('en-IN')} rating${totalStudioRatings === 1 ? '' : 's'}`
              }
              icon={<Star sx={{ color: colors.primary }} />}
              iconBg={`${colors.primary}20`}
            />
            <StatCard
              title="Average class rating"
              value={
                averageSessionRating == null
                  ? '—'
                  : averageSessionRating.toFixed(1)
              }
              subtitle={
                totalSessionRatings === 0
                  ? 'no class ratings yet'
                  : `from ${totalSessionRatings.toLocaleString('en-IN')} rating${totalSessionRatings === 1 ? '' : 's'}`
              }
              icon={<Star sx={{ color: colors.secondary }} />}
              iconBg={`${colors.secondary}20`}
            />
            <StatCard
              title="Total reviews"
              value={totalReviews.toLocaleString('en-IN')}
              subtitle="reviews submitted"
              icon={<RateReview sx={{ color: colors.secondary }} />}
              iconBg={`${colors.secondary}20`}
            />
            {/* Bars are the studio-rating distribution, so they must be scaled
                by the number of studio ratings — not by the review count, which
                now includes reviews that rated only the class. */}
            <RatingBreakdownCard breakdown={ratingBreakdown} total={totalStudioRatings} />
          </>
        )}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2, borderRadius: 2, border: `1px solid ${colors.divider}` }}>
        <CardContent sx={{ p: 2.5, pb: 2 }}>
          <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
            <TextField
              placeholder="Search by user, studio, or review…"
              value={searchInput}
              onChange={handleSearchChange}
              size="small"
              sx={{ flex: 1, minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Rating</InputLabel>
              <Select
                value={ratingFilter}
                label="Rating"
                onChange={e => handleRatingFilterChange(e.target.value)}
              >
                <MenuItem value="all">All ratings</MenuItem>
                <MenuItem value="5">★★★★★ 5</MenuItem>
                <MenuItem value="4">★★★★ 4</MenuItem>
                <MenuItem value="3">★★★ 3</MenuItem>
                <MenuItem value="2">★★ 2</MenuItem>
                <MenuItem value="1">★ 1</MenuItem>
              </Select>
            </FormControl>
            {/* Pointless for a studio user — the server pins them to their own
                studio, so the picker could only ever return the same list. */}
            {!isStudioUser && (
            <Autocomplete
              options={studios}
              getOptionLabel={option => option.studioName || ''}
              value={studios.find(s => s._id === studioFilter) || null}
              onChange={(_, newValue) => handleStudioFilterChange(newValue?._id || '')}
              size="small"
              sx={{ minWidth: 220, flex: 1 }}
              renderInput={params => (
                <TextField {...params} label="Studio" placeholder="All studios" />
              )}
              isOptionEqualToValue={(option, value) => option._id === value._id}
            />
            )}
          </Box>
          {!isLoading && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              Showing {reviews.length} of {listTotal} review{listTotal !== 1 ? 's' : ''}
              {fiveStarCount > 0 && ` · ${fiveStarCount} five-star review${fiveStarCount !== 1 ? 's' : ''}`}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 2, border: `1px solid ${colors.divider}` }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: colors.backgroundTertiary }}>
                <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                {!isStudioUser && (
                  <TableCell sx={{ fontWeight: 700 }}>Studio</TableCell>
                )}
                <TableCell sx={{ fontWeight: 700 }}>Class</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Studio rating</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Class rating</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Review</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : reviews.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box textAlign="center" py={6}>
                      <RateReview sx={{ fontSize: 56, color: colors.textSecondary, mb: 1.5 }} />
                      <Typography variant="h6" gutterBottom>
                        No reviews found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {appliedSearch || ratingFilter !== 'all' || studioFilter
                          ? 'Try adjusting your search or filters.'
                          : 'Reviews will appear here once users submit them.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                reviews.map(review => (
                  <TableRow key={review._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.25}>
                        <Avatar
                          src={review.user?.image}
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: getAvatarColor(review.user?.name),
                            fontSize: '0.75rem',
                          }}
                        >
                          {getUserInitials(review.user?.name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {review.user?.name || 'Unknown user'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {review.user?.email || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    {!isStudioUser && (
                      <TableCell>
                        <Typography variant="body2">
                          {review.studio?.studioName || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2">
                        {review.session?.className || '—'}
                      </Typography>
                      {review.instructor?.name ? (
                        <Typography variant="caption" color="text.secondary">
                          {review.instructor.name}
                        </Typography>
                      ) : review.session ? (
                        // The class is known from the booking, but who taught it
                        // is not: the instructor is only recorded on reviews left
                        // since the two-part rating, and is never inferred after
                        // the fact — a reassigned session would attribute an old
                        // review to the wrong person.
                        <Typography variant="caption" color="text.disabled">
                          instructor not recorded
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {/* Both scores are optional: a member may rate the class
                          and leave the studio blank, or the reverse. An unrated
                          score is NOT a zero and is never drawn as empty stars —
                          it is absent from the row and from every average. */}
                      {review.rating ? (
                        <StarRatingDisplay rating={review.rating} />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          not given
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Also absent on every review left before the two-part
                          rating shipped. Same rule: not given, never zero. */}
                      {review.sessionRating ? (
                        <StarRatingDisplay rating={review.sessionRating} />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          not given
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" color="text.secondary">
                        {truncateText(review.review)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatJoinedDate(review.createdAt)}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && totalPages > 1 && (
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              borderTop: `1px solid ${colors.divider}`,
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default ReviewsPage;
